import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react";
import { Card, Layout,Col, Button, Form, Row, Radio, Comment, Divider, Input, Tooltip, Spin } from "antd";

import useRootStore from "../../store/useRootStore";

import FileUpload from "../Shared/Forms/FileUpload";
import ImageGallery from "../ImageGallery";
import { DeleteOutlined, LoadingOutlined } from "@ant-design/icons";

// partials
import { FullScreen } from "../Shared/Layouts/FullScreen";
import { Titlebar } from "../Plate/partials";

//import Titlebar from "./partials/Titlebar";
import { EditorWrapper } from "../Plate";
import { PdfChapterEndnotes } from "../Previewer/print/types";
import EditTOC from "./EditTOC";
import { getTitleChapterData } from "../../utils/volumes";
import { parseFormattableEndnote } from "../../press/exportEpub/parsers/formattableEndNote";
import { GetThemeFromIDB } from "../../utils/offline.book.helpers";
import { Button as AtticusButton, ButtonSize } from "../Shared/Buttons/Button";
import { AtticusRadioButton } from "../Shared/Form/FormControls";
import { SyncIcon, UploadIcon, UploadIconTitlePage, InfoIcon } from "../../content/icons";
import { AtticusDivider } from "../Shared/Divider";
import { DeleteImageButton } from "../Shared/Buttons";
import { EditorRightMenu } from "./RightMenu/EditorRightMenu";
import { ScrollContainer } from "../Shared/Layouts";

const isWithoutEditor = (v: string): v is IChapterStore.ChapterTypeWithoutEditor =>
  ["half-title", "title", "toc", "endnotes", "image", "part", "volume"].includes(v);

const EditButton = () => {
    const { setBookEdit } = useRootStore().bookStore;
    return (
        <Button className="btn-a" type="primary" onClick={() => setBookEdit(true)}>
            {"Edit book details"}
        </Button>
    );
};

const ConvertButton = () => {
  const { chapter, saveChapterMetaUpdates } = useRootStore().bookStore;
  const { refreshCache } = useRootStore().pdfCacheStore;

  const convertToFullPageImage = () => {
    saveChapterMetaUpdates({
      ...chapter,
      type: "image",
      configuration: { showInTableOfContents: false },
    });
    // invalidate only the current chapter
    refreshCache(chapter.bookId, "full-page-image-chapter-change", {
      "full-page-image-chapter-change": { chapterId: chapter._id },
    });
  };

  return (
    <AtticusButton
      type="at-secondary"
      size="small"
      color="blue"
      onClick={() => convertToFullPageImage()}
    >
      {"Convert to full page image"}
    </AtticusButton>
  );
};

const FullPageImage = observer(() => {
    const { chapter, debouncedSaveChapterMetaUpdates } = useRootStore().bookStore;
    const { refreshCache } = useRootStore().pdfCacheStore;

    const [image, setImage] = useState(chapter.fullpageImage?.imageUrl ? chapter.fullpageImage.imageUrl : "");
    const [uploading, setUploading] = useState(false);
    const [printExtent, setPrintExtent] = useState(
        chapter.fullpageImage?.printExtent
            ? chapter.fullpageImage.printExtent
            : "margins"
    );
    const [verticalAlignment, setVerticalAlignment] = useState(
        chapter.fullpageImage?.verticalAlignment
            ? chapter.fullpageImage.verticalAlignment
            : "center"
    );

    const [imageSize, setImageSize] = useState("");
    const [imageName, setImageName] = useState("");

    const [imageGalleryUrl, setImageGalleryUrl] = useState("");

    useEffect(() => {
        setImage(chapter.fullpageImage?.imageUrl ? chapter.fullpageImage.imageUrl : "");
        setPrintExtent(chapter.fullpageImage?.printExtent ? chapter.fullpageImage.printExtent : "margins");
        setVerticalAlignment(chapter.fullpageImage?.verticalAlignment ? chapter.fullpageImage.verticalAlignment : "center");
    }, [chapter._id, chapter.fullpageImage]);

    const getIGURL = (link) => {
        setImage(link);
        setImageGalleryUrl(link);
    };

    const debouncedUpdates = () => {
         debouncedSaveChapterMetaUpdates({
            ...chapter,
            fullpageImage: {
                imageUrl: image,
                printExtent: printExtent,
                verticalAlignment: verticalAlignment,
            },
        });
        refreshCache(chapter.bookId, "full-page-image-chapter-change", { "full-page-image-chapter-change": { chapterId: chapter._id } });
    };

    const getImageSize = async () => {
      const response = await fetch(image);
      const contentLength = response.headers.get("Content-Length");
      
      if (contentLength) {
        const sizeInBytes = parseInt(contentLength, 10);
        const sizeInMb = sizeInBytes / (1024 * 1024);
        setImageSize(sizeInMb.toFixed(2) + "mb");
      }
    };

    const extractImageName =(url) =>{
      const urlSegments = url.split("/");
      const lastSegment = urlSegments[urlSegments.length - 1];
      if (/\.(jpg|jpeg|png)$/.test(lastSegment)) {
        setImageName(lastSegment);
      }
    };

    useEffect(()=> {
      debouncedUpdates();
    }, [imageGalleryUrl]);

    useEffect(()=>{
      getImageSize();
      extractImageName(image);
    }, [image]);

    return (
      <Layout.Content className="fullpage-img-layout">
        <>
          <div className={chapter.title === "Title Page"? "title-page-area scroller": "fullpage-img-area scroller"}>
            <Form layout="vertical">
              <Titlebar
                onEnter={() => ({})}
                customTitle={false}
                placeholder="Full Page Image"
                autofocus={false}
                sceneIndex={null}
                sceneTitle={null}
                handleSceneTitleOnChange={() => ({})}
                chapterConfiguratorVariant={"image"}
                isTitle= {true}
              />
              <Form
                layout="vertical"
                className="title-page-form"
              >
                <Row className="title-page-body-wrapper">
                  <Col span={12} className="title-page-container">
                    <div className="image-toolbar-body-wrapper">
                      <ImageGallery 
                        handleImageUrl={getIGURL}
                        renderButtonProps={{
                          type: "at-primary",
                          backgroundColor: "green",
                          size: ButtonSize.MEDIUM,
                          block: true
                        }}
                      />
                      <AtticusDivider className="title-page-divider">
                        <p className="image-toolbar-divider-text">Or</p>
                      </AtticusDivider>
                      <Row className="title-page-text-holder">
                        <h4 className="image-toolbar-upload-header">
                          Upload a new image
                        </h4>
                      </Row>
                        {image ? (
                          <>
                            <div
                              className="image-toolbar-image-previewer title-page-image-previewer"
                              style={{ backgroundImage: `url(${image})` }}
                            >
                              <div className="image-toolbar-image-previewer-container">
                                <DeleteImageButton onClick={() => {
                                  setImage("");
                                  debouncedSaveChapterMetaUpdates({
                                      ...chapter,
                                      fullpageImage: {
                                      imageUrl: "",
                                      printExtent: printExtent,
                                      verticalAlignment: verticalAlignment,
                                    },
                                  });
                                  refreshCache(chapter.bookId, "full-page-image-chapter-change", { "full-page-image-chapter-change": { chapterId: chapter._id } });
                                }}/>
                              </div>
                            </div> 
                            <div className="image-toolbar-upload-footer">
                              <p className="image-toolbar-upload-footer-text">
                                {imageName}
                                
                              </p>
                              <p className="image-toolbar-upload-footer-text">
                                {imageSize}
                              </p>
                            </div>
                          </>
                        ) : (
                          <div>
                            <Row className="title-page-file-upload-container">
                              <Col span={24} className="image-upload-wrapper">
                                <Form.Item
                                  name="coverImg"
                                  valuePropName="fileList"
                                  noStyle
                                >
                                  <FileUpload
                                    folder="cover-images"
                                    onFileUploadEnd={() => setUploading(false)}
                                    onFileUploadStart={() => setUploading(true)}
                                    fileType="image/png, image/jpg, image/jpeg"
                                    onFileUpload={(url) => {
                                      if (!url) return;
                                      setImage(url || "");
                                      debouncedSaveChapterMetaUpdates({
                                        ...chapter,
                                        fullpageImage: {
                                          imageUrl: url,
                                          printExtent: printExtent,
                                          verticalAlignment: verticalAlignment,
                                        },
                                      });
                                      refreshCache(chapter.bookId, "full-page-image-chapter-change", { "full-page-image-chapter-change": { chapterId: chapter._id } });
                                    }}
                                    styleOverrides={{
                                      backgroundColor: "#E7EDF8",
                                      borderRadius: 4,
                                      borderWidth: 2,
                                      borderStyle: "dashed",
                                      borderColor: "#ADC1E3",
                                    }}
                                  >
                                    <div className="image-toolbar-dnd-container">
                                      {uploading ? (
                                        <Spin
                                        indicator={
                                          <LoadingOutlined className="edit-book-spinner" />
                                        }
                                      />
                                      ) : (
                                        <>
                                          <span>
                                            <UploadIcon color="#3363B9" />
                                          </span>
                                          <p>
                                            <span className="image-toolbar-dnd-text-highlight">
                                              Click to upload image
                                            </span>{" "}
                                            or drag and drop
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  </FileUpload>
                                </Form.Item>
                                {!uploading? <div className="image-toolbar-upload-footer">
                                  <p className="image-toolbar-upload-footer-text">
                                    Supported formats: PNG, JPG
                                  </p>
                                  <p className="image-toolbar-upload-footer-text">
                                    Maximum size: 10mb
                                  </p>
                                </div> : <></>}
                              </Col>
                            </Row>
                          </div>
                        )}
                    </div>
                    <Row className="title-page-form-container">
                      <Col span={24}>
                        <Row>
                            Print extent
                            <Tooltip 
                              placement="bottom" 
                              title={<span>Full bleed image should match the page trim size plus the bleed allowance.</span>}
                            >
                          <div className="info-icon-wrapper">
                            <InfoIcon />
                          </div>
                        </Tooltip>
                        </Row>
                      </Col>
                      <Form.Item className="at-form-item">
                        <Radio.Group
                          value={printExtent}
                          onChange={(value) => {
                            if (value.target.value) {
                              setPrintExtent(value.target.value);
                              debouncedSaveChapterMetaUpdates({
                                ...chapter,
                                fullpageImage: {
                                  imageUrl: image,
                                  printExtent: value.target
                                    .value as any,
                                  verticalAlignment: verticalAlignment,
                                },
                              });
                              refreshCache(chapter.bookId, "full-page-image-chapter-change", { "full-page-image-chapter-change": { chapterId: chapter._id } });
                            }
                          }}
                        >
                          <AtticusRadioButton
                            type="secondary"
                            value="margins"
                          >
                            Margins
                          </AtticusRadioButton>
                          <AtticusRadioButton
                            type="secondary"
                            value="full-bleed"
                          >
                            Full Bleed
                          </AtticusRadioButton>
                        </Radio.Group>
                      </Form.Item>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Row>
                            Vertical alignment
                            <Tooltip 
                              placement="bottom" 
                              title={<span>Vertical alignment will only be applied to the print version (PDF).</span>}
                            >
                              <div className="info-icon-wrapper">
                                <InfoIcon />
                              </div>
                            </Tooltip>
                        </Row>
                      </Col>
                      <Col>
                        <Form.Item className="at-form-item">
                          <Radio.Group
                            disabled={
                              printExtent === "full-bleed" ? true : false
                            }
                            value={verticalAlignment}
                            onChange={(value) => {
                              setVerticalAlignment(value.target.value);
                              debouncedSaveChapterMetaUpdates({
                                ...chapter,
                                fullpageImage: {
                                  imageUrl: image,
                                  printExtent: printExtent,
                                  verticalAlignment: value.target
                                    .value as any,
                                },
                              });
                              refreshCache(chapter.bookId, "full-page-image-chapter-change", { "full-page-image-chapter-change": { chapterId: chapter._id } });
                            }}
                          >
                            <AtticusRadioButton
                              type="secondary"
                              value="top"
                            >
                              Top
                            </AtticusRadioButton>
                            <AtticusRadioButton
                              type="secondary"
                              value="center"
                            >
                              Center
                            </AtticusRadioButton>
                            <AtticusRadioButton
                              type="secondary"
                              value="bottom"
                            >
                              Bottom
                            </AtticusRadioButton>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            </Form>
          </div>
        </>
      </Layout.Content>
    );
});

const ExtraView = (props) => {
  const { book, getAllEndNotesByChapter } = useRootStore().bookStore;
  const { setSidebarPreviewerOnlyMode, setSettingView, editor_setting } = useRootStore().appStore;
  const { refreshCache } = useRootStore().pdfCacheStore;
  const [endNotes, setEndNotes] = useState<(PdfChapterEndnotes)[]>([]);
  const [theme, setTheme] = useState<(IThemeStore.ThemeStyleProps)>();
  const [chapterMetadataForm] = Form.useForm();
  const { chapter, debouncedSaveChapterMetaUpdates } = useRootStore().bookStore;

  const { titleType, publisherName, title, subtitle, author, publisherLink } = useMemo(() => getTitleChapterData(book, chapter), [chapter, book]);

  useEffect(()=>{
    /** 
     * 1) Set the RHS Sidebar to the Previewer only mode for isWithoutEditor() chapters.
     * 2) Hide the RHS Sidebar unless the Previewer is selected when switching the chapter to a isWithoutEditor() chapter.
     * */
    setSidebarPreviewerOnlyMode(true);
    setSettingView({
      show: editor_setting.view === "previewer" ? editor_setting.show : false,
      view: "previewer",
    });
    
    getAllEndNotesByChapter().then(notes=>{
      setEndNotes(notes);
    });
    const getTheme = async () => {
      const theme = await GetThemeFromIDB(book.themeId);
      setTheme(theme?.properties);
    };
    getTheme();
  }, []);

  useEffect(()=>{
    // this is to update form values when the chapter changes,
    // since lib won't update the them automatically
    chapterMetadataForm.resetFields();
  },[chapter]);
 
  function handleChange(_, values){
    debouncedSaveChapterMetaUpdates({
      ...chapter,
      title: values.title,
      subtitle: values.subtitle,
      ...(values.author !== undefined && chapter.volume
        ? {
            volume: {
              ...chapter.volume,
              author: values.author,
            },
          }
        : {}),
    },false);
    refreshCache(chapter.bookId, "chapter-title-change", { "chapter-title-change":{ chapterId: chapter._id }});
  }
  const CommentBlock = ({ label, value }) => (
    <Comment
      author={<span className="title-page-comment-label">{label}</span>}
      content={<h4 className="title-page-content-label">{value || "-"}</h4>}
    />
  );

  switch (props.type) {
    case "title":
      return (
        <Layout.Content>
          <Card className="title-page-card">
            <Row className="title-page-card-content">
              <div className="container-profile">
                <Col span={12}>
                  <h4>
                    Title page is auto-generated based on contents of the{" "}
                    {titleType} 
                  </h4>
                </Col>
                <Col span={12} className="title-page-card-button-container">
                  <ConvertButton />
                  {titleType === "book" && <EditButton />}
                </Col>
              </div>
            </Row>
          </Card>
          <div className="title-page-area">
          <ScrollContainer>
            <div className="att-stats potted">
              <h2>Title Page</h2>
              <CommentBlock
                label={(titleType === "book" ? "" : titleType + " ") + "Title"}
                value={title}
              />
              <CommentBlock
                label={
                  (titleType === "book" ? "" : titleType + " ") + "Subtitle"
                }
                value={subtitle}
              />
              {(titleType === "book" || titleType === "volume") && (
                <CommentBlock
                  label={"Author"}
                  value={author.join(", ") || "-"}
                />
              )}
              {titleType === "book" && (
                <>
                  <CommentBlock label={"Publisher name"} value={publisherName} />
                  <CommentBlock
                    label={"Publisher link"}
                    value={publisherLink}
                  />
                </>
              )}
            </div>
          </ScrollContainer>
          </div>
        </Layout.Content>
      );
    case "toc":
      return  <EditTOC {...props}/>;
    case "image":
      return <FullPageImage />;
    case "endnotes":
      return (
        <Layout.Content>
          <Card>
            <Form
              form={chapterMetadataForm}
              onValuesChange={handleChange}
              initialValues={{ title: chapter.title }}
            >
              <Form.Item name="title" label="Custom endnotes title">
                <Input />
              </Form.Item>
            </Form>
            <div className="att-toc">
              <h3>
                Endnotes are auto-generated based on the endnotes used in the
                book
              </h3>
            </div>
          </Card>
          
          <div className="att-toc-list potted">
            {endNotes.map((endNote, i) => (
              <div key={i}>
                <div >{endNote.chapterTitle} </div>
                {endNote.chapterEndnotes.map((chapterEndnotes, j) => (

                  <div className="item" key={j}>
                    <span className="name">
                      {chapterEndnotes.type === "endnote" && (
                        <div className="endnote-wrapper">
                          {`${j + 1}. `}
                          <div className="endnote"
                            dangerouslySetInnerHTML={{
                              __html: parseFormattableEndnote(chapterEndnotes.note, chapter, theme),
                            }}
                          ></div>
                        </div>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Layout.Content>
      );
      case "part":
        return (
          <Layout.Content>
            <Card>
              <div className="att-toc">
                <h3>A Part will <b>NOT</b> introduce a page to the book. However it has a "Title" chapter</h3>
              </div>
            </Card>
            <Divider />
            <div className="potted">
              <Form
                form={chapterMetadataForm}
                onValuesChange={handleChange}
                labelCol={{ xs: 24, sm: 6, md: 6, lg: 7, xl: 7 }}
                wrapperCol={{ xs: 24, sm: 18, md: 16, lg: 12, xl: 12 }}
                initialValues={{
                  title: chapter.title,
                  subtitle: chapter.subtitle,
                }}
              >
                <Form.Item name="title" label="Part title">
                  <Input />
                </Form.Item>
                <Form.Item name="subtitle" label="Part subtitle">
                  <Input />
                </Form.Item>
              </Form>
            </div>
          </Layout.Content>
        );
        case "volume":
          return (
            <Layout.Content>
              <Card>
                <div className="att-toc">
                  <h3>A Volume will <b>NOT</b> introduce a page to the book. However it has "Title" & "Table of Contents" chapters</h3>
                </div>
              </Card>
              <Divider />
              <div className="potted">
                <Form
                  form={chapterMetadataForm}
                  onValuesChange={handleChange}
                  labelCol={{ xs: 24, sm: 6, md: 6, lg: 7, xl: 7 }}
                  wrapperCol={{ xs: 24, sm: 18, md: 16, lg: 12, xl: 12 }}
                  initialValues={{
                    title: chapter.title,
                    subtitle: chapter.subtitle,
                    author: chapter.volume?.author,
                  }}
                >
                  <Form.Item name="title" label="Volume title">
                    <Input />
                  </Form.Item>
                  <Form.Item name="subtitle" label="Volume subtitle">
                    <Input />
                  </Form.Item>
                  <Form.Item name="author" label="Volume author">
                    <Input />
                  </Form.Item>
                </Form>
              </div>
            </Layout.Content>
          );
  }
  return null;
};

export const EditChapter = observer(({ isChapterTemplate }: { isChapterTemplate: boolean, chapterTemplateId: string | null }) => {
    const { book, chapter } = useRootStore().bookStore;
    const { writing, editor_setting, sidebarPreviewerOnlyMode, chapterTemplateView } = useRootStore().appStore;
    const { push } = useHistory();

    useEffect(() => {
        if(!isChapterTemplate) push(`#chapter=${chapter._id}`);
    }, [chapter._id]);

    return (
      <FullScreen className={sidebarPreviewerOnlyMode ? "writing-section" : ""}>
        { isWithoutEditor(chapter.type) && !isChapterTemplate? (
          <div className="extra-content">
            <ExtraView
              chapter={chapter}
              type={chapter.type}
              currentBook={book}
            />
          </div>
        ) : (
          <EditorWrapper />
        )}

        {writing && editor_setting.show && sidebarPreviewerOnlyMode && !chapterTemplateView ? (
          <Col
            id="sidebar-previewer-only"
            flex={editor_setting.view === "previewer" ? "" : "320px"}
            className="setting-area-wrapper"
          >
            <EditorRightMenu view={editor_setting.view} />
          </Col>
        ) : null}
      </FullScreen>
    );
});
